import { useReducer } from "react";
import {
  type GenericSchema,
  type GenericSchemaAsync,
  type InferInput,
  type InferOutput,
  object,
  safeParse,
  string,
} from "valibot";

const errorSchema = object({
  error: string(),
});

type State<T extends GenericSchema> = {
  isLoading: boolean;
  data: InferOutput<T> | null;
  error: Error | null;
};

const initialState = {
  isLoading: false,
  data: null,
  error: null,
};

type ACTIONTYPE<T extends GenericSchema> =
  | { type: "START" }
  | { type: "SUCCESS"; payload: InferOutput<T> }
  | { type: "FAILED"; payload: Error };

function reducer<T extends GenericSchema>(_: State<T>, action: ACTIONTYPE<T>): State<T> {
  switch (action.type) {
    case "START":
      return {
        isLoading: true,
        data: null,
        error: null,
      };
    case "SUCCESS":
      return {
        isLoading: false,
        data: action.payload,
        error: null,
      };
    case "FAILED":
      return {
        isLoading: false,
        data: null,
        error: action.payload,
      };
    default:
      throw new Error();
  }
}

export type Options<Output> = {
  onCompleted?: (data: Output) => void;
};

export const useMutation = <InputSchema extends GenericSchema | GenericSchemaAsync, OutputSchema extends GenericSchema>(
  url: string,
  outputSchema: OutputSchema,
  options?: Options<InferOutput<OutputSchema>>,
) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const mutate = async (
    input: InferInput<InputSchema>,
  ): Promise<{
    data?: InferOutput<OutputSchema>;
    error?: string;
  }> => {
    if (state.isLoading) return {};

    dispatch({
      type: "START",
    });

    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(input),
      });

      if (response.ok) {
        const result = await response.json();
        const data = safeParse(outputSchema, result);

        if (data.success) {
          dispatch({
            type: "SUCCESS",
            payload: data.output,
          });
          options?.onCompleted?.(data.output);
          return {
            data: data.output,
          };
        }

        dispatch({
          type: "FAILED",
          payload: new Error("failed to parse response"),
        });
        return {
          error: "failed to parse response",
        };
      }

      const result = await response.json();
      const data = safeParse(errorSchema, result);

      if (data.success) {
        dispatch({
          type: "FAILED",
          payload: new Error(data.output.error),
        });
        return {
          error: data.output.error,
        };
      }

      dispatch({
        type: "FAILED",
        payload: new Error("failed to parse response"),
      });
      return {
        error: "failed to parse response",
      };
    } catch (e) {
      dispatch({
        type: "FAILED",
        payload: new Error("error fetching"),
      });
      return {
        error: "failed to parse response",
      };
    }
  };

  return [mutate, { ...state }] as const;
};
